import { getRegisteredGlobal, registerGlobal } from "@trumpf/ux.utils";
import { IconMetaData, IconProps, IconSetType, IconThemeType } from "./types";
import { AVAILABLE_ICONS, NS_ICON_REGISTRY } from "./constants";
import { fixAssetsPath } from "./utils";
import { debounce } from "../../utils/debounce";
import { logger } from "../../utils/console";

/**
 * Interface for the IconRegistry, providing methods and properties for icon management.
 */
export interface IconRegistryType {
    /**
     * The resolved path to the icon assets directory.
     */
    readonly assetsPath: string;
    /**
     * The current icon theme (e.g., 'web' or 'responsive').
     */
    readonly theme: IconThemeType;
    /**
     * Returns all registered icons, including built-in and custom icons.
     */
    getRegisteredIcons: <T extends IconMetaData>() => T[];
    /**
     * Resolves the asset URL for a given icon's properties.
     */
    resolveAssetUrl: (iconProps: IconProps) => string | null;
    /**
     * Registers a single custom icon.
     */
    registerIcon: (iconData: IconMetaData) => IconRegistryType;
    /**
     * Registers a set of icons under a namespace.
     */
    registerIconSet: (iconSet: IconSetType) => IconRegistryType;
    /**
     * Sets the path to the icon assets directory.
     */
    setAssetsPath: (path: string) => IconRegistryType;
    /**
     * Sets the icon theme explicitly.
     */
    setTheme: (value: IconThemeType) => IconRegistryType;
}

/**
 * IconRegistry manages icon registration, theme detection, asset path resolution, and icon updates.
 */
class IconRegistry implements IconRegistryType {
    /**
     * Internal path to icon assets, detected or set explicitly.
     */
    private assetsPathInternal: string | undefined;
    /**
     * Explicitly set path to icon assets.
     */
    private assetsPathExplicit: string | undefined;
    /**
     * Explicitly set icon theme.
     */
    private themeExplicit: IconThemeType;
    /**
     * Theme detected from the DOM.
     */
    private themeDetected: IconThemeType;
    /**
     * Array of custom registered icons.
     */
    private readonly customIcons: IconMetaData[] = [];
    /**
     * Registered icon sets by namespace.
     */
    private readonly iconSets: Record<string, IconSetType> = {};

    /**
     * Initializes the registry, detects theme, and sets up theme change listener.
     */
    constructor() {
        this.themeDetected = this.detectTheme();
        document.addEventListener("uxThemeChange", this.handleThemeChange);
    }

    /**
     * Handles theme change events and updates icons if needed.
     */
    private handleThemeChange = debounce(() => {
        const oldValue = this.assetsPath;
        this.assetsPathInternal = this.assetsPathExplicit;
        this.themeDetected = this.detectTheme();
        if (this.assetsPath !== oldValue) {
            this.updateIcons();
        }
    }, 0);

    /**
     * Returns the current icon theme, either explicit or detected.
     */
    public get theme(): IconThemeType {
        if (this.themeExplicit !== undefined) {
            return this.themeExplicit;
        }
        if (this.themeDetected === undefined) {
            this.themeDetected = this.detectTheme();
        }
        return this.themeDetected;
    }

    /**
     * Sets the icon theme explicitly.
     * @param value The theme to set ('web' or 'responsive').
     */
    public setTheme(value: IconThemeType): IconRegistryType {
        this.themeExplicit = value;
        return this;
    }

    /**
     * Returns the resolved path to the icon assets directory.
     */
    public get assetsPath(): string {
        if (this.assetsPathInternal === undefined) {
            this.assetsPathInternal = this.detectAssetsPath();
        }
        return fixAssetsPath(this.assetsPathInternal);
    }

    /**
     * Sets the path to the icon assets directory and updates icons if changed.
     * @param value The new assets path.
     */
    public setAssetsPath(value: string): IconRegistryType {
        const oldValue = this.assetsPath;
        this.assetsPathExplicit = fixAssetsPath(value);
        this.assetsPathInternal = this.assetsPathExplicit;
        if (this.assetsPath !== oldValue) {
            this.updateIcons();
        }
        return this;
    }

    /**
     * Registers a single custom icon. Warns if the icon is already registered.
     * @param iconData The icon metadata to register.
     */
    public registerIcon(iconData: IconMetaData): IconRegistryType {
        try {
            const item = this.getRegisteredIcons().find((el) => el.name === iconData.name) || null;
            if (item !== null) {
                logger.warn(`Warning: Icon ${iconData.name} is already registered`);
            } else {
                this.customIcons.push(iconData);
                logger.info(`Icon ${iconData.name} registered successfully.`);
            }
        } catch (error) {
            logger.error(`Error registering icon:`, error);
        }
        return this;
    }

    /**
     * Registers a set of icons under a namespace. Warns if the set is already registered.
     * @param iconSet The icon set to register.
     */
    public registerIconSet(iconSet: IconSetType): IconRegistryType {
        try {
            if (this.iconSets[iconSet.ns] !== undefined) {
                logger.warn(`Warning: IconSet ${iconSet.ns} is already registered`);
            } else {
                this.iconSets[iconSet.ns] = iconSet;
                this.updateIcons();
                logger.info(`IconSet ${iconSet.ns} registered successfully.`);
            }
        } catch (error) {
            logger.error(`Error registering icon set:`, error);
        }
        return this;
    }

    /**
     * Returns all registered icons, including built-in and custom icons.
     */
    public getRegisteredIcons<T extends IconMetaData>(): T[] {
        try {
            return [...AVAILABLE_ICONS[this.themeDetected], ...this.customIcons] as T[];
        } catch (error) {
            logger.error(`Error getting registered icons:`, error);
            return [] as T[];
        }
    }

    /**
     * Resolves the asset URL for a given icon's properties.
     * @param iconProps The icon properties.
     */
    public resolveAssetUrl(iconProps: IconProps): string | null {
        try {
            const { name } = iconProps;
            if (name.includes(":")) {
                return this.getIconSetAssetUrl(iconProps);
            }
            const fileName = this.getIconBaseName(iconProps);
            if (fileName === null) {
                logger.warn(`Icon base name could not be resolved for:`, iconProps);
                return null;
            }
            return `${fixAssetsPath(this.assetsPath)}${fileName}.svg`;
        } catch (error) {
            logger.error(`Error resolving asset URL:`, error);
            return null;
        }
    }

    /**
     * Returns the base file name for an icon, considering legacy names and weights.
     * @param iconProps The icon properties.
     */
    private getIconBaseName = (iconProps: IconProps): string | null => {
        try {
            let fileName: string | null = null;
            const registeredIcons = this.getRegisteredIcons();
            let iconMetaData = registeredIcons.find((icon) => icon.name === iconProps.name);
            if (iconMetaData !== undefined) {
                const isFilled =
                    (iconProps.filled || (iconMetaData.outlineWeights ?? []).length === 0) &&
                    (iconMetaData.filledWeights ?? []).length > 0;
                const weights = isFilled ? iconMetaData.filledWeights : iconMetaData.outlineWeights;
                fileName = this.getIconVariantName({
                    name: iconProps.name,
                    filled: isFilled,
                    // @ts-expect-error Check for typing
                    weight: this.getIconWeight(iconProps.weight, weights)
                });
            } else {
                iconMetaData = registeredIcons.find((icon) => icon.legacyNames?.[iconProps.name] !== undefined);
                if (!iconMetaData?.legacyNames?.[iconProps.name]) {
                    logger.warn(`Icon meta data not found for:`, iconProps);
                    return fileName;
                }
                const isFilled = iconMetaData.legacyNames[iconProps.name].filled;
                const weights = (isFilled ? iconMetaData.filledWeights : iconMetaData.outlineWeights) ?? [];
                fileName = this.getIconVariantName({
                    name: iconMetaData.name,
                    filled: isFilled,
                    weight: this.getIconWeight(iconMetaData.legacyNames[iconProps.name].weight ?? "", weights)
                });
            }
            return fileName || null;
        } catch (error) {
            logger.error(`Error getting icon base name:`, error);
            return null;
        }
    };

    /**
     * Resolves the asset URL for an icon from a registered icon set.
     * @param iconProps The icon properties.
     */
    private getIconSetAssetUrl = (iconProps: IconProps): string | null => {
        try {
            const parts = iconProps.name.split(":");
            if (parts.length !== 2) {
                logger.warn(`Invalid icon set name:`, iconProps.name);
                return null;
            }
            const iconSet = this.iconSets[parts[0]];
            if (iconSet === undefined) {
                logger.warn(`Icon set not found:`, parts[0]);
                return null;
            }
            const baseName = this.getIconVariantName({
                ...iconProps,
                name: parts[1]
            });
            return `${fixAssetsPath(iconSet.assetsPath)}${baseName}.svg`;
        } catch (error) {
            logger.error(`Error getting icon set asset URL:`, error);
            return null;
        }
    };

    /**
     * Detects the current icon theme from the DOM.
     */
    private detectTheme = (): "web" | "responsive" => {
        return document.getElementsByClassName("ux-theme-responsive").length > 0 ? "responsive" : "web";
    };

    /**
     * Detects the path to the icon assets directory from the DOM or falls back to a default.
     */
    private detectAssetsPath = (): string => {
        let detectorEl: HTMLDivElement | null = null;
        try {
            detectorEl = document.createElement("div");
            detectorEl.className = "ux-icon__svg-path-detector";
            document.body.appendChild(detectorEl);
            let svgPath: string;
            try {
                svgPath = getComputedStyle(detectorEl)
                    .backgroundImage.replace(/url\("?/, "")
                    .replace(/[a-zA-Z0-9_.-]*"?\)$/, "");
            } catch {
                svgPath = `/themes/${this.theme}/assets/icons/svg/`;
            }
            return fixAssetsPath(svgPath);
        } catch (error) {
            logger.error("Error detecting assets path:", error);
            return `/themes/${this.theme}/assets/icons/svg/`;
        } finally {
            if (detectorEl) {
                detectorEl.remove();
            }
        }
    };

    /**
     * Updates all icons in the DOM by triggering reloads.
     */
    private updateIcons = debounce(() => {
        try {
            document.querySelectorAll(`ux-icon.hydrated`).forEach((el) => {
                (el as HTMLUxIconElement).reload();
            });
            document.querySelectorAll(`ux-button, ux-tag`).forEach((el) => {
                const shadowRoot = (el as HTMLElement).shadowRoot;
                if (shadowRoot === null) {
                    return;
                }
                shadowRoot.querySelectorAll(`ux-icon.hydrated`).forEach((iconEl) => {
                    (iconEl as HTMLUxIconElement).reload();
                });
            });
            logger.info("Icons updated.");
        } catch (error) {
            logger.error("Error updating icons:", error);
        }
    }, 10);

    /**
     * Returns the variant name for an icon, including filled and weight suffixes.
     * @param name Icon name
     * @param filled Whether the icon is filled
     * @param weight Icon weight
     */
    private getIconVariantName = ({ name, filled, weight }: IconProps): string => {
        const parts = [name];
        if (filled) {
            parts.push(`filled`);
        }
        if (typeof weight === "string" && weight !== "") {
            parts.push(weight);
        }
        return parts.join(`--`);
    };

    /**
     * Determines the best matching weight for an icon from available weights.
     * @param weight Desired weight
     * @param availableWeights List of available weights
     */
    private getIconWeight = (weight: string, availableWeights: string[]): string => {
        if (!Array.isArray(availableWeights)) {
            return "";
        }
        if (availableWeights.includes(weight)) {
            return weight;
        } else if (availableWeights.includes("normal")) {
            return "normal";
        } else if (availableWeights.includes("light")) {
            return "light";
        } else if (availableWeights.includes("bold")) {
            return "bold";
        }
        return "";
    };
}

/**
 * Returns a singleton instance of the IconRegistry.
 */
export const getIconRegistry = (): IconRegistryType => {
    let instance = getRegisteredGlobal<IconRegistryType>(NS_ICON_REGISTRY);
    if (typeof instance === "undefined") {
        instance = new IconRegistry();
        registerGlobal(instance, NS_ICON_REGISTRY);
    }
    return instance;
};
